import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import styled, { createGlobalStyle, css } from 'styled-components';
import { ExternalLink } from 'next/components/ui';
import { IconWrapper, ItemIcon, Separator } from './styled';

// NOTE: There is some launchpad manipulation code that is probably not the very best
// but help us move forward with launchpad styles/behaviour until we have the sdk
// updated.
// - Mutation Observer: To determine whether the launchpad is open/closed to add
//   a style in the menu. This is listening to the whole body since the launchpad is
//   added as body child. If we find this to cause issues, we can remove it.
// - Manually clicking on the launchpad overlay to close it after clicking on a footer
//   element
// TODO: Once the launchpad changes are addressed, that code can be changed

const LAUNCHPAD_ID = 'appcues-launchpad';
const LAUNCHPAD_CONTAINER_CLASS = 'appcues';
const LAUNCHPAD_ACTIVE_CLASS = 'appcues-widget-container appcues-active';
const LAUNCHPAD_OVERLAY = '.appcues-widget-backdrop';
const ASK_SUPPORT_ID = 'ask-support-opener';

const LaunchpadStyle = createGlobalStyle`
  /* Launchpad styles */
  .appcues {
    .appcues-widget-dropdown.appcues-widget-fixed.appcues-widget-top-right {
      border-radius: 6px;
      box-shadow: var(--dropdown-menu-shadow);
      margin: 0;
      padding: 0;
      position: fixed;
      transform: translateY(10px) translateX(-10px);

      &:after, &:before {
        border: solid transparent;
        content: "";
        height: 0;
        left: -10px;
        position: absolute;
        top: 10%;
        width: 0;
      }

      &:after {
        border-right-color: var(--white);
        border-width: 10px;
      }
    }

    .appcues-widget-icon {
      height: 40px;
      left: 0;
      margin-left: 0;
      opacity: 1; /* override widget fade-in */
      position: absolute;
      top: 0;
      width: 100%;

      &.appcues-icon-visible::after,
      &.appcues-icon-visible::before {
        display: none;
      }
    }

    .appcues-widget-dropdown .appcues-widget-content {
      overflow-y: auto;
      padding-top: 6px;
    }

    .appcues-widget-container {
      li.appcues-nothing-new {
        display: none;
      }

      a[data-itemid] time {
        display: none;
      }
    }

    .appcues-widget-list {
      border-bottom: none;

      li {
        &.appcues-read,
        &.appcues-unread {
          color: var(--heading-primary);
          font-weight: normal;
          position: relative;
        }

        &:before {
          background-color: var(--primary);
          border-radius: 50%;
          content: ' ';
          height: 8px;
          left: 20px;
          position: absolute;
          top: calc(50% - 4px);
          width: 8px;
        }

        a {
          align-items: center;
          display: flex;
          min-height: 40px;
          padding: 4px 20px 4px 40px;
  
          &:hover {
            background-color: var(--nav-item-hover);
          }
        }
      }
    }
  }

  /* Helpscout beacon: Make it appear above the launchpad */
  #beacon-container .BeaconContainer {
    z-index: 1000000001;
  }
`;

const HelpIcon = styled(ItemIcon)`
  ${({ open }) =>
    open &&
    `
    color: var(--primary);
  `}
`;

const Text = styled.span`
  font-weight: var(--normal);

  ${({ open }) =>
    open &&
    `
      font-weight: var(--bold);
  `}
`;

const FooterItems = styled.ul`
  list-style-type: none;
`;

const FooterItem = styled.li`
  height: 40px;
`;

const baseLinkStyles = css`
  align-items: center;
  color: var(--heading-primary);
  display: flex;
  font-weight: var(--normal);
  padding-left: 20px;

  &:hover {
    background-color: var(--nav-item-hover);
  }
`;

const FooterLink = styled(ExternalLink).attrs({
  'data-close-launchpad': '1',
})`
  ${baseLinkStyles}

  height: 100%;
  text-decoration: none;

  &:hover {
    color: var(--heading-primary);
  }
`;

const AskSupport = styled.button`
  ${baseLinkStyles}

  background: transparent;
  border: none;
  height: 40px;
  width: 100%;
`;

const FooterSeparator = styled(Separator)`
  margin: 6px 0;
`;

const Wrapper = styled.li`
  align-items: center;
  display: flex !important; /* To override the launchpad style */
  height: 40px;
  padding-left: 36px;
  width: 100%;

  &:hover {
    background-color: var(--nav-item-hover);
  }
`;

const Footer = () => {
  return (
    <>
      <FooterSeparator />
      <FooterItems>
        <FooterItem>
          <FooterLink href="https://www.appcues.com/whats-new">
            What&apos;s new
          </FooterLink>
        </FooterItem>
        <FooterItem>
          <FooterLink href="https://docs.appcues.com/">Help docs</FooterLink>
        </FooterItem>
        <FooterItem>
          <AskSupport id={ASK_SUPPORT_ID} data-close-launchpad="1">
            Ask support
          </AskSupport>
        </FooterItem>
      </FooterItems>
    </>
  );
};

const closeLaunchpad = () => {
  // Since the launchpad does not provide a method to close it,
  // manually click on the launchpad overlay to get that done.
  document.querySelector(LAUNCHPAD_OVERLAY).click();
};

const HelpMenu = ({ user = {} }) => {
  const [open, setOpen] = useState(false);

  // target: div.appcues-widget-backdrop
  // appcues-widget-container appcues-active
  const handleLaunchpadOpened = mutations => {
    try {
      mutations.forEach(mutation => {
        if (mutation.target.className === LAUNCHPAD_CONTAINER_CLASS) {
          if (mutation.addedNodes[0]?.className === LAUNCHPAD_ACTIVE_CLASS) {
            setOpen(true);
          } else if (
            mutation.removedNodes[0]?.className === LAUNCHPAD_ACTIVE_CLASS
          ) {
            setOpen(false);
          }
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const footer = renderToStaticMarkup(<Footer />);

    const handleLaunchpadClick = event => {
      if (event.target.dataset.closeLaunchpad === '1') {
        closeLaunchpad();
      }

      if (event.target.id === ASK_SUPPORT_ID) {
        try {
          /* eslint-disable new-cap */
          window.Beacon('prefill', {
            name: user.fullname,
            email: user.email,
            subject: '',
            text: '',
          });
          window.Beacon('navigate', '/ask/');
          window.Beacon('open');
          /* eslint-enable new-cap */
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    };

    if (window.Appcues) {
      const observer = new MutationObserver(handleLaunchpadOpened);

      window.Appcues.loadLaunchpad(`#${LAUNCHPAD_ID}`, {
        position: 'top-right',
        footer,
      });

      // Manually attach the listener to "Ask support" button
      // That resides in the footer.
      document.addEventListener('click', handleLaunchpadClick);

      // Manually observe for the launchpad node to
      // Determine if it's open or closed
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        document.removeEventListener('click', handleLaunchpadClick);
        observer.disconnect();
      };
    }
  }, [user]);

  return (
    <Wrapper id={LAUNCHPAD_ID}>
      <LaunchpadStyle />
      <IconWrapper>
        <HelpIcon icon="comment" title="Help" open={open} />
      </IconWrapper>
      <Text open={open}>Help</Text>
    </Wrapper>
  );
};

// TODO: User probably will have its own shape in the entities
HelpMenu.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    fullname: PropTypes.string,
  }),
};

export default HelpMenu;
